import type { LucideIcon } from 'lucide-react'

import {
    Accessibility,
    AudioLines,
    Backpack,
    BookHeart,
    BookOpen,
    Building2,
    CalendarCheck,
    Camera,
    ClipboardCheck,
    ClipboardPaste,
    Ear,
    FileSpreadsheet,
    FileText,
    Flame,
    Footprints,
    Gift,
    Globe,
    GraduationCap,
    HeartHandshake,
    Hourglass,
    House,
    Layers,
    LayoutDashboard,
    LayoutGrid,
    Library,
    ListChecks,
    Map,
    MessageCircleQuestion,
    Microscope,
    MonitorSmartphone,
    Presentation,
    Printer,
    Puzzle,
    RotateCcw,
    School,
    ScrollText,
    Search,
    ShieldCheck,
    Smartphone,
    Sprout,
    Target,
    Type,
    UserRound,
    Users,
    Utensils,
    Volume2,
    Zap
} from 'lucide-react'

import {
    CONTACT_EMAIL,
    GROEPEN,
    WENSEN
} from '@/constants/aanmelden'

export { CONTACT_EMAIL, GROEPEN, WENSEN }

export const LEESRITME_URL = 'https://leesritme.nl'
export const KVK_NUMMER = '85635081'
export const VIDEO_URL = 'https://leesritme.cdn.modulify.website/video/leesritme-uitleg.mp4'
export const VIDEO_POSTER = '/images/video-uitleg.jpg'
export const ROMAN_PHOTO = '/images/roman.webp'

export const external = (path: string): string => `${LEESRITME_URL}${path}`

const mailto = (subject: string): string => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`

export const MAILTO_GESPREK = mailto('Gesprek over Leesritme voor onze school')
export const MAILTO_BESTUUR = mailto('Leesritme voor meerdere scholen')
export const MAILTO_VRAAG = `mailto:${CONTACT_EMAIL}`

export const DOWNLOADS = {
    mrTeam: '/downloads/leesritme-informatie-mr-en-team.pdf',
    startgids: '/downloads/leesritme-startgids.pdf',
    proefweek: '/downloads/leesritme-proefweek-oceaan-groep6.pdf'
}

export interface NavItem {
    label: string
    href: string
    description?: string
    icon?: LucideIcon
}

export interface IconItem {
    title: string
    text: string
    icon: LucideIcon
}

export interface SourceLink {
    label: string
    href: string
}

export const NAV_LINKS: Array<NavItem> = [
    { label: "De oplossing", href: '/#oplossing' },
    { label: "Wat je krijgt", href: '/#aanbod' },
    { label: "Prijzen", href: '/#prijzen' }
]

export const VERANTWOORDING_LINKS: Array<NavItem> = [
    { label: "Didactiek", description: "Waarom het werkt, met alle bronnen", href: external('/verantwoording'), icon: Microscope },
    { label: "Kerndoelen", description: "Aansluiting op kerndoelen en 1F/2F", href: external('/kerndoelen'), icon: Target },
    { label: "Privacy en veiligheid", description: "Welke gegevens, waar, wie ziet wat", href: external('/privacy'), icon: ShieldCheck },
    { label: "Wat als het stopt", description: "Gegevens terug en verwijderd", href: external('/privacy#stoppen'), icon: RotateCcw },
    { label: "Toegankelijkheid", description: "Voor elk kind bruikbaar", href: external('/toegankelijkheid'), icon: Accessibility },
    { label: "Wie maakt Leesritme", description: "Contact en KvK", href: external('/privacy#wie'), icon: UserRound },
    { label: "Informatiebrief (pdf)", description: "Eén A4 voor MR en team", href: DOWNLOADS.mrTeam, icon: FileText }
]

export const LOGIN_LINKS: Array<NavItem> = [
    { label: "Leerling", description: "De leesapp, met je inlogkaartje", href: external('/app'), icon: Backpack },
    { label: "Leerkracht", description: "Het leerkrachtportaal", href: external('/portaal'), icon: LayoutDashboard }
]

export const HERO_BADGES: Array<string> = [
    "Gratis pilot, zonder verplichting",
    "Opgezet in vijf minuten",
    "Privacy geregeld",
    "Gebouwd op onderzoek"
]

export const THEME_TITLES: Array<string> = [
    "Bijen en bloemen",
    "Vulkanen en aardbevingen",
    "De Vikingen",
    "Het oude Egypte",
    "De oceaan",
    "Ridders en kastelen",
    "De race naar de maan",
    "Geheimschrift en spionnen",
    "Dinosaurussen",
    "Piraten en kapers",
    "De Gouden Eeuw",
    "Het regenwoud",
    "Forensisch speurwerk",
    "Thuis op het water",
    "Nieuws, nepnieuws en AI-beelden",
    "Vogels op reis",
    "De Maya’s",
    "Kinderrechten",
    "Duikers en onderzeeërs",
    "Van graan tot brood",
    "Extreem weer",
    "Het brein en slaap",
    "Droge voeten: dijken en gemalen",
    "Kijk eens naar de maan"
]

export const PISA_DATA: Array<{ year: string, value: number }> = [
    { year: '2015', value: 18 },
    { year: '2018', value: 24 },
    { year: '2022', value: 33 },
    { year: '2025', value: 39 }
]

export const PISA_SOURCES: Array<SourceLink> = [
    { label: "Probiblio", href: 'https://www.probiblio.nl/pisa-paniek/' },
    { label: "Universiteit Twente", href: 'https://www.utwente.nl/en/about-us/news-events-ceremonies/news/2026/9/1144409/the-new-pisa-results-student-performance-in-maths-and-science-unchanged-reading-literacy-declines-further' }
]

export const STATS: Array<{ value: string, label: string, source: SourceLink }> = [
    {
        value: "74%",
        label: "haalt eind groep 8 het streefniveau lezen 2F.",
        source: { label: "Inspectie van het Onderwijs, De Staat van het Onderwijs 2026", href: 'https://www.rijksoverheid.nl/documenten/rapporten/2026/04/15/destaatvanhetonderwijs2026' }
    },
    {
        value: "37%",
        label: "in groep 6 leest met weinig of geen plezier.",
        source: { label: "PIRLS 2021, via Stichting Lezen", href: 'https://www.lezen.nl/nieuws/resultaten-pirls-2021-daling-leesvaardigheid-nu-ook-zichtbaar-in-het-basisonderwijs/' }
    },
    {
        value: "Geen",
        label: "aantoonbaar verschil tussen leesmethodes.",
        source: { label: "Kennisrotonde: effect van leesmethodes", href: 'https://www.kennisrotonde.nl/vraag-en-antwoord/effect-leesmethodes-op-leesvaardigheid-leerlingen-basis-en-voortgezet-onderwijs' }
    }
]

export const PAIN_POINTS: Array<IconItem> = [
    { title: "Te weinig leeskilometers", text: "Alleen de leestijd op school is vaak niet genoeg om echt vlot en met begrip te leren lezen.", icon: Footprints },
    { title: "Goede oefenteksten zoeken kost veel tijd", text: "Teksten vinden, beoordelen en nakijken past niet in een volle week.", icon: Hourglass },
    { title: "Thuis lezen is moeilijk vol te houden", text: "Na schooltijd winnen schermen het vaak van het boek.", icon: Smartphone },
    { title: "Geen ruimte voor weer een nieuwe methode", text: "Geen overstap, geen scholing en geen extra nakijkwerk erbij.", icon: Layers }
]

export const SETUP_STEPS: Array<{ title: string, text: string, icon: LucideIcon }> = [
    { title: "Klas aanmaken", text: "Geef je klas een naam en kies de groep.", icon: School },
    { title: "Klassenlijst plakken", text: "Uit ParnasSys, Esis of Excel.", icon: ClipboardPaste },
    { title: "Inlogkaartjes printen", text: "En je klas kan beginnen.", icon: Printer }
]

export const PILLARS: Array<IconItem & { note: string }> = [
    {
        title: "Elke dag lezen wordt eenvoudig",
        text: "Elke dag staat er een tekst van tien minuten klaar. De themaweek is al ingericht en elke maandag verschijnt vanzelf de volgende.",
        note: "Je koopt geen extra content, maar een dagelijkse leesroutine.",
        icon: CalendarCheck
    },
    {
        title: "School en thuis in één systeem",
        text: "Thuis wacht elke dag een tweede tekst. Ouders loggen in met een eigen code en krijgen een vraag voor aan tafel, of een seintje als hun kind nog niet heeft gelezen.",
        note: "Jij ziet wat er op school én thuis is gelezen.",
        icon: House
    },
    {
        title: "Nauwelijks extra werk voor de leerkracht",
        text: "Teksten, vragen en antwoorden staan klaar en de app kijkt na. In één overzicht zie je wie heeft gelezen.",
        note: "Een aanvulling, geen nieuw programma dat ingevoerd moet worden.",
        icon: ClipboardCheck
    },
    {
        title: "Rijke teksten die kennis opbouwen",
        text: "Echte onderwerpen met echte foto’s. Vijf samenhangende teksten per week, in reeksen van vier weken over één groot onderwerp, aansluitend bij de zaakvakken.",
        note: "238 themaweken voor groep 3 tot en met 8.",
        icon: Sprout
    }
]

export const ROOSTER_SLOTS: Array<IconItem> = [
    { title: "Zelfstandig lezen", text: "Gebruik tien minuten van de stilleestijd. Een vaste tekst met vragen helpt het begrip meer dan alleen stillezen. Het leesboek houdt de rest van de tijd.", icon: BookOpen },
    { title: "Weektaak", text: "Leerlingen volgen elke dag zelfstandig dezelfde route. In het portaal zie je wie klaar is.", icon: ListChecks },
    { title: "Zaakvakken", text: "Kies een reeks die past bij het onderwerp van je zaakvakles. Zo bereidt het lezen de les voor.", icon: Globe },
    { title: "Huiswerk", text: "De thuistekst staat elke dag klaar. Geen kopiëren, geen nakijken, en je ziet wat er thuis is gelezen.", icon: House },
    { title: "Klaar-opdracht", text: "Snelle lezers lezen de tekst van de dag. Eén per dag, dus geen eindeloze stapel.", icon: Zap }
]

export interface AppScreen {
    id: string
    title: string
    text: string
    src: string
    alt: string
    icon: LucideIcon
}

export const APP_SCREENS: Array<AppScreen> = [
    {
        id: 'voorlezen',
        title: "Voorlezen",
        text: "De app leest voor en elk woord kleurt mee. Tik op een woord om daar verder te lezen.",
        src: '/images/app/app-meelezen.webp',
        alt: "Voorlezen: de app leest de tekst Een stad vol bijen voor, met de knoppen Pauze en Stop",
        icon: Volume2
    },
    {
        id: 'leesmanieren',
        title: "Vijf manieren van lezen",
        text: "Zelf lezen, lees voor, echo-lezen, koorlezen en lees hardop. Elk kind leest op een manier die past.",
        src: '/images/app/app-leesmanieren.webp',
        alt: "Keuzescherm met vijf manieren van lezen: zelf lezen, lees voor, echo-lezen, koorlezen en lees hardop",
        icon: AudioLines
    },
    {
        id: 'onderwerpen',
        title: "Echte onderwerpen",
        text: "Rijke teksten met echte foto’s, zoals het verhaal over de Watersnoodramp van 1953.",
        src: '/images/app/app-lezen.webp',
        alt: "Leesscherm met de tekst Een schip als sluisdeur over de Watersnoodramp, met een foto uit 1953",
        icon: Camera
    },
    {
        id: 'vragen',
        title: "Vragen bij de tekst",
        text: "Fout antwoord? De alinea met het antwoord licht op, zodat je terugzoekt in de tekst.",
        src: '/images/app/app-vraag.webp',
        alt: "Vraag Waar komt het zout in de zee vandaan? Het foute antwoord is doorgestreept en de alinea met het antwoord licht geel op",
        icon: MessageCircleQuestion
    },
    {
        id: 'detective',
        title: "Lees als een detective",
        text: "Speuren naar hoe de schrijver het zegt. Voor groep 5 tot en met 8.",
        src: '/images/app/app-detective.webp',
        alt: "Lees als een detective, ronde 2: welk woord laat je de schimmel bijna voelen? In de tekst is het woord pluizig aangetikt",
        icon: Search
    },
    {
        id: 'leesreeks',
        title: "Leesreeks",
        text: "Elke dag lezen laat de leesreeks groeien, met een herinnering als je nog niet hebt gelezen.",
        src: '/images/app/app-reeks.webp',
        alt: "Startscherm met de dagen van de week, een leesreeks van 6 dagen en een herinnering om 16:30",
        icon: Flame
    },
    {
        id: 'boek',
        title: "Van scherm naar boek",
        text: "Bij elk thema tips om verder te lezen in een echt boek uit de bieb.",
        src: '/images/app/app-boek.webp',
        alt: "Scherm Lees verder in een echt boek met boekentips bij het thema Bijen en bloemen",
        icon: BookHeart
    },
    {
        id: 'missie',
        title: "Leesmissie",
        text: "Met pijltjes zoek je de schatkist, met de missierol als aanwijzing.",
        src: '/images/app/app-missie.webp',
        alt: "Leesmissie in de diepzee: met pijltjes zoek je de schatkist, met de missierol als aanwijzing",
        icon: Map
    },
    {
        id: 'boekenkast',
        title: "Boekenkast van de klas",
        text: "Raad een tekst of boek aan en ontdek wat klasgenoten mooi vonden.",
        src: '/images/app/app-boekenkast.webp',
        alt: "Boekenkast van de klas met een knop Raad iets aan en aanraders van klasgenoten",
        icon: Library
    }
]

export const THEME_WEEKS: Array<{ groep: number, weken: number }> = [
    { groep: 3, weken: 39 },
    { groep: 4, weken: 30 },
    { groep: 5, weken: 30 },
    { groep: 6, weken: 47 },
    { groep: 7, weken: 46 },
    { groep: 8, weken: 46 }
]

export interface AudienceFeature {
    title: string
    text: string
    icon: LucideIcon
}

export const AUDIENCE_PUPILS: Array<AudienceFeature> = [
    { title: "Elke dag een tekst klaar", text: "Een tekst in de klas, en thuis vanzelf een tweede tekst over hetzelfde thema.", icon: CalendarCheck },
    { title: "Hulp bij lezen", text: "Voorlezen, echo-lezen en een app die meeluistert als je hardop leest.", icon: Ear },
    { title: "Lezen zoals het past", text: "Letters en regelafstand zijn instelbaar, met steun bij dezelfde rijke tekst.", icon: Type }
]

export const AUDIENCE_TEACHERS: Array<AudienceFeature> = [
    { title: "Geen voorbereiding, geen nakijkwerk", text: "Met elke week een lesbrief en een gesprekskaart.", icon: ClipboardCheck },
    { title: "Leesdashboard: zien wie leest", text: "Per kind op school en thuis, welke vraagsoorten lastig zijn en een korte leescheck.", icon: LayoutDashboard },
    { title: "Niveau en huiswerk per kind", text: "In een paar klikken ingesteld, met export naar Excel.", icon: FileSpreadsheet }
]

export const AUDIENCE_PARENTS: Array<AudienceFeature> = [
    { title: "Een vraag voor aan tafel", text: "Over de tekst die hun kind die dag las.", icon: Utensils },
    { title: "Tips om mee te luisteren", text: "Voor als hun kind thuis hardop leest.", icon: Ear },
    { title: "Samen lezen", text: "Voorleesverhalen met voorbeeldvragen voor groep 1 tot en met 3.", icon: BookOpen },
    { title: "Een eigen oudercode", text: "Geen account nodig: de code staat in de brief van school.", icon: Users }
]

export const AUDIENCE_SCHOOL: Array<AudienceFeature> = [
    { title: "Schooloverzicht", text: "Per klas zien wie las, op school en thuis.", icon: Building2 },
    { title: "Resultaat voor de directie", text: "Eén A4 per klas, zonder namen van kinderen.", icon: FileText },
    { title: "Zorgvuldig met gegevens", text: "Verwerkersovereenkomst, opslag in de EU en geen trackers.", icon: ShieldCheck },
    { title: "Gratis hulp bij het starten", text: "Een startgids en een informatiebrief voor MR en team.", icon: HeartHandshake }
]

export const AUDIENCE_YOUNGEST: Array<AudienceFeature & { link: SourceLink }> = [
    {
        title: "Samen lezen",
        text: "Korte prentenverhalen voor thuis, met een oudervraag per pagina en vaak een tip.",
        icon: BookHeart,
        link: { label: "Bekijk Samen lezen", href: external('/voorlezen') }
    },
    {
        title: "Klankspel",
        text: "Rijmen, beginklanken horen, klanken samenvoegen en woorden bouwen. Op het digibord, een tablet of thuis.",
        icon: Puzzle,
        link: { label: "Bekijk het klankspel", href: external('/klanken') }
    }
]

export interface ResearchItem {
    title: string
    text: string
    inApp: string
    sources: Array<SourceLink>
}

export const RESEARCH: Array<ResearchItem> = [
    {
        title: "Kennis opbouwen in themaweken",
        text: "Lezen rond één onderwerp bouwt kennis op, en die kennis helpt kinderen beter begrijpen wat ze lezen. Dat laat een meta-analyse van 35 studies zien.",
        inApp: "Vijf samenhangende teksten per week, in reeksen van vier weken.",
        sources: [{ label: "Hwang e.a., 2021", href: 'https://doi.org/10.1080/10888438.2021.1954005' }]
    },
    {
        title: "Herhaald hardop lezen, met luisterhulp",
        text: "Een tekst vaker hardop lezen, met een goed voorbeeld om naar te luisteren, helpt kinderen vloeiender te lezen.",
        inApp: "Voorlezen, echo-lezen en lees hardop, met meekleurende woorden.",
        sources: [
            { label: "National Reading Panel, 2000", href: 'https://www.nichd.nih.gov/sites/default/files/publications/pubs/nrp/Documents/ch3.pdf' },
            { label: "Lee & Yoon, 2017", href: 'https://doi.org/10.1177/0022219415605194' }
        ]
    },
    {
        title: "Feedback die terugwijst naar de tekst",
        text: "Uitleg bij een antwoord helpt meer dan alleen horen of het goed of fout is.",
        inApp: "Fout antwoord? De alinea met het antwoord licht op.",
        sources: [{ label: "Van der Kleij e.a., 2015", href: 'https://doi.org/10.3102/0034654314564881' }]
    },
    {
        title: "Ouders als luisteraar",
        text: "Als ouders thuis meeluisteren en meepraten, steunt dat het lezen van hun kind.",
        inApp: "Een vraag voor aan tafel en tips om mee te luisteren.",
        sources: [{ label: "Dahl-Leonard e.a., 2025", href: 'https://doi.org/10.1007/s10648-025-09985-3' }]
    }
]

export const PRICE_PER_PUPIL = 6.99
export const PRICE_LATER = 13.99

export const PILOT_FEATURES: Array<string> = [
    "Volledige toegang voor je klas",
    "Al het lesmateriaal",
    "Hulp bij het starten",
    "Samen de resultaten bekijken",
    "Stoppen kan altijd"
]

export const SCHOOL_FEATURES: Array<string> = [
    "Alle groepen, alle themaweken",
    "Leesdashboard en schooloverzicht",
    "Oudercodes voor thuis lezen",
    "Verwerkersovereenkomst en opslag in de EU"
]

export const INCLUDED_ITEMS: Array<{ label: string, icon: LucideIcon }> = [
    { label: "Startbegeleiding door Roman", icon: HeartHandshake },
    { label: "Leesreview na acht weken", icon: ClipboardCheck },
    { label: "Ouderkit", icon: Gift },
    { label: "Leesmuur", icon: LayoutGrid },
    { label: "Leesplan", icon: ScrollText },
    { label: "Schermtijdgids", icon: MonitorSmartphone },
    { label: "Presentatie voor de ouderavond", icon: Presentation }
]

export const ROLES: Array<{ value: string, label: string, icon: LucideIcon }> = [
    { value: 'Leerkracht', label: "Leerkracht", icon: GraduationCap },
    { value: 'Directeur', label: "Directeur", icon: School },
    { value: 'Schoolbestuur', label: "Schoolbestuur", icon: Building2 },
    { value: 'Ouder', label: "Ouder", icon: House }
]

export interface FooterColumn {
    title: string
    links: Array<NavItem>
}

export const FOOTER_COLUMNS: Array<FooterColumn> = [
    {
        title: "Product",
        links: [
            { label: "Leerlingdemo", href: external('/demo') },
            { label: "Alle themaweken", href: external('/themaweken') },
            { label: "Voorbeeldportaal", href: external('/leerkracht') },
            { label: "Prijzen", href: '/#prijzen' },
            { label: "Pilot aanvragen", href: '/#aanmelden' }
        ]
    },
    {
        title: "Voor wie",
        links: [
            { label: "Leerkrachten", href: external('/leerkrachten') },
            { label: "Directie", href: external('/directie') },
            { label: "Besturen", href: external('/bestuur') },
            { label: "Ouders", href: external('/ouders') },
            { label: "Ouderscherm (oudercode)", href: external('/ouder') }
        ]
    },
    {
        title: "Verantwoording",
        links: [
            { label: "Didactiek en onderzoek", href: external('/verantwoording') },
            { label: "Kerndoelen", href: external('/kerndoelen') },
            { label: "Privacy en veiligheid", href: external('/privacy') },
            { label: "Toegankelijkheid", href: external('/toegankelijkheid') }
        ]
    },
    {
        title: "Downloads",
        links: [
            { label: "Informatie voor MR en team", description: "pdf", href: DOWNLOADS.mrTeam },
            { label: "Startgids voor leerkrachten", description: "pdf", href: DOWNLOADS.startgids },
            { label: "Proefweek De oceaan", description: "groep 6, pdf", href: DOWNLOADS.proefweek }
        ]
    }
]

export const FIGURE_SOURCES: Array<SourceLink> = [
    { label: "Inspectie van het Onderwijs, De Staat van het Onderwijs 2026", href: 'https://www.rijksoverheid.nl/documenten/rapporten/2026/04/15/destaatvanhetonderwijs2026' },
    { label: "PIRLS 2021, via Stichting Lezen", href: 'https://www.lezen.nl/nieuws/resultaten-pirls-2021-daling-leesvaardigheid-nu-ook-zichtbaar-in-het-basisonderwijs/' },
    { label: "PISA, via Probiblio", href: 'https://www.probiblio.nl/pisa-paniek/' },
    { label: "Kennisrotonde: effect van leesmethodes", href: 'https://www.kennisrotonde.nl/vraag-en-antwoord/effect-leesmethodes-op-leesvaardigheid-leerlingen-basis-en-voortgezet-onderwijs' }
]
